import { forwardRef } from "react";
import i18n from "../../i18n";

/**
 * ShareCard
 * ----------
 * Premium parchment-on-navy card used as the off-screen render target
 * for image and (single-page) PDF export. One card to rule them all —
 * verse-of-the-day, parasha, aggadah, story, and lesson previews.
 *
 * Visual language matches the landing page brand kit:
 *   - Navy background (#1a2b4a) with subtle radial parchment glows
 *   - Hairline gold frame (#c9a449)
 *   - Brand mark at the top ("תוריא · Torahya")
 *   - Body content (controlled by `body` prop — can be plain string,
 *     array of paragraphs, or arbitrary JSX)
 *   - Footer with torahya.com + tagline
 *
 * Sizing
 *   - "square": 1080×1080  (Instagram / WhatsApp status)
 *   - "portrait": 1080×1350 (Instagram portrait)
 *   - "a4": 794×1123       (matches A4 at 96dpi — used as PDF source)
 *
 * The card is rendered into a fixed off-screen position by the caller
 * so it never paints to the user's screen. html-to-image scrapes it.
 */

const SIZES = {
  square:   { w: 1080, h: 1080 },
  portrait: { w: 1080, h: 1350 },
  a4:       { w: 794,  h: 1123 },
};

const ShareCard = forwardRef(function ShareCard(
  {
    size = "square",
    eyebrow,           // "פסוק היום · ד׳ בסיון תשפ״ו"
    title,             // headline shown in gold
    body,              // string | array of strings | JSX
    citation,          // small ref under body — e.g. "בראשית א:א" or "תלמוד · ברכות ב.""
    secondary,         // optional english/aramaic translation
    footnote,          // optional extra line (e.g. "המשך באפליקציה" for lesson previews)
    truncated,         // when true, append "המשך באפליקציה · torahya.com"
    lang,              // optional override; defaults to current i18n language
  },
  ref
) {
  const dims = SIZES[size] || SIZES.square;
  // Resolve the effective language at render time. The card is rendered
  // off-screen by the parent right before html-to-image scrapes it, so
  // we can safely read i18n.language synchronously. Hebrew remains the
  // default whenever lang is missing / unrecognised — protects the
  // existing Hebrew share flow byte-for-byte.
  const effLang = lang === "en" || lang === "he" ? lang : (i18n?.language === "en" ? "en" : "he");
  const isEn = effLang === "en";
  const cardDir = isEn ? "ltr" : "rtl";
  const continueInApp = isEn ? "Continue in the app · torahya.com" : "המשך באפליקציה · torahya.com";
  const tagline = isEn ? "Your personal Beit Midrash" : "בית המדרש האישי שלך";
  const gold     = "#c9a449";
  const goldSoft = "rgba(201, 164, 73, 0.35)";
  const navy     = "#1a2b4a";
  const cream    = "#f3e8c8";
  const creamMuted = "rgba(243, 232, 200, 0.72)";

  const bodyBlock = Array.isArray(body)
    ? body.filter(Boolean).map((p, i) => (
        <p key={i} style={{ margin: i === 0 ? 0 : "0.7em 0 0", fontSize: bodyFontSize(body, dims), lineHeight: 1.7 }}>
          {p}
        </p>
      ))
    : typeof body === "string"
      ? <p style={{ margin: 0, fontSize: bodyFontSize(body, dims), lineHeight: 1.7 }}>{body}</p>
      : body;

  return (
    <div
      ref={ref}
      data-testid="share-card"
      dir={cardDir}
      style={{
        width: dims.w,
        height: dims.h,
        position: "relative",
        overflow: "hidden",
        padding: dims.w >= 1000 ? 88 : 56,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        fontFamily: '"Frank Ruhl Libre", "David Libre", "Noto Serif Hebrew", serif',
        background: `radial-gradient(ellipse at 18% 8%, rgba(255, 245, 215, 0.07) 0%, transparent 55%), radial-gradient(ellipse at 82% 92%, rgba(201, 164, 73, 0.10) 0%, transparent 60%), linear-gradient(180deg, ${navy} 0%, #14233f 100%)`,
        color: cream,
      }}
    >
      {/* gold hairline frame */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: dims.w >= 1000 ? 36 : 24,
          border: `1px solid ${gold}`,
          boxShadow: `inset 0 0 0 4px transparent, inset 0 0 0 5px ${goldSoft}`,
          pointerEvents: "none",
          borderRadius: 2,
        }}
      />

      {/* corner stars */}
      {[
        { top: dims.w >= 1000 ? 24 : 18, left: dims.w >= 1000 ? 24 : 18 },
        { top: dims.w >= 1000 ? 24 : 18, right: dims.w >= 1000 ? 24 : 18 },
        { bottom: dims.w >= 1000 ? 24 : 18, left: dims.w >= 1000 ? 24 : 18 },
        { bottom: dims.w >= 1000 ? 24 : 18, right: dims.w >= 1000 ? 24 : 18 },
      ].map((pos, i) => (
        <span
          key={i}
          aria-hidden
          style={{
            position: "absolute",
            ...pos,
            color: gold,
            fontSize: 22,
            lineHeight: 1,
            fontFamily: "serif",
          }}
        >
          ✦
        </span>
      ))}

      {/* Header — brand */}
      <header
        style={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          marginTop: dims.w >= 1000 ? 16 : 8,
        }}
      >
        <div
          style={{
            fontFamily: '"Cormorant Garamond", "Frank Ruhl Libre", serif',
            fontStyle: "italic",
            letterSpacing: 10,
            textTransform: "uppercase",
            fontSize: 18,
            color: gold,
          }}
        >
          Torahya · תוריא
        </div>
        {eyebrow && (
          <div
            style={{
              marginTop: 18,
              fontFamily: '"Heebo", "Frank Ruhl Libre", sans-serif',
              fontSize: 15,
              letterSpacing: 4,
              color: creamMuted,
            }}
          >
            {eyebrow}
          </div>
        )}
        <div
          style={{
            marginTop: 14,
            height: 1,
            width: 120,
            marginLeft: "auto",
            marginRight: "auto",
            background: `linear-gradient(90deg, transparent, ${gold}, transparent)`,
          }}
        />
      </header>

      {/* Body */}
      <main
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: dims.w >= 1000 ? "0 12px" : "0 6px",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {title && (
          <h1
            style={{
              fontSize: titleFontSize(title, dims),
              lineHeight: 1.25,
              margin: "0 0 28px",
              fontWeight: 600,
              textAlign: "center",
              color: cream,
            }}
          >
            {title}
          </h1>
        )}
        <div
          data-testid="share-card-body"
          style={{
            color: cream,
            textAlign: "center",
            fontSize: 28,
            lineHeight: 1.7,
          }}
        >
          {bodyBlock}
        </div>
        {secondary && (
          <p
            style={{
              marginTop: 26,
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: 19,
              lineHeight: 1.55,
              color: creamMuted,
              direction: "ltr",
              textAlign: "center",
            }}
          >
            {`“${secondary}”`}
          </p>
        )}
        {citation && (
          <div
            style={{
              marginTop: 32,
              textAlign: "center",
              color: gold,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: 1,
            }}
          >
            {citation}
          </div>
        )}
        {truncated && (
          <div
            style={{
              marginTop: 22,
              textAlign: "center",
              color: creamMuted,
              fontSize: 16,
              fontStyle: "italic",
            }}
          >
            {continueInApp}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer
        style={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          marginBottom: dims.w >= 1000 ? 16 : 8,
        }}
      >
        <div
          style={{
            fontSize: 16,
            letterSpacing: 8,
            color: gold,
            marginBottom: 14,
          }}
        >
          ✦ ❖ ✦
        </div>
        <div
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: "italic",
            fontSize: 17,
            letterSpacing: 4,
            color: creamMuted,
          }}
        >
          torahya.com
        </div>
        {footnote && (
          <div
            style={{
              marginTop: 8,
              fontSize: 14,
              color: creamMuted,
              fontStyle: "italic",
            }}
          >
            {footnote}
          </div>
        )}
        {!footnote && (
          <div
            style={{
              marginTop: 8,
              fontSize: 14,
              color: creamMuted,
              fontStyle: "italic",
            }}
          >
            {tagline}
          </div>
        )}
      </footer>
    </div>
  );
});

/** Heuristic — verse-length-aware title sizing so long titles still fit. */
function titleFontSize(text, dims) {
  const t = typeof text === "string" ? text : "";
  const base = dims.w >= 1000 ? 56 : 36;
  if (t.length <= 20) return base;
  if (t.length <= 40) return Math.round(base * 0.85);
  if (t.length <= 60) return Math.round(base * 0.72);
  return Math.round(base * 0.6);
}

function bodyFontSize(body, dims) {
  const text = Array.isArray(body) ? body.filter(Boolean).join(" ") : (typeof body === "string" ? body : "");
  const len = text.length || 0;
  const base = dims.w >= 1000 ? 38 : 24;
  if (len < 120) return base;
  if (len < 260) return Math.round(base * 0.85);
  if (len < 500) return Math.round(base * 0.7);
  if (len < 900) return Math.round(base * 0.58);
  return Math.round(base * 0.5);
}

export default ShareCard;
